import { useState, useContext, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../authentication/AuthContext";
import Layout from "../shared/Layout";
import ThemeOption from "./ThemeOption";
import { useQuery } from "react-query";

const UserEdit = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.user_metadata?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [loading, setLoading] = useState(false);

  const fetchUser = async (userId?: string) => {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  };

  const {
    data: profile,
    isLoading,
    isError,
  } = useQuery(["user", user?.id], () => fetchUser(user?.id), {
    enabled: !!user?.id,
  });

  const handleUpdateProfile = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from("users")
        .update({
          username: name,
        })
        .eq("id", user?.id);

      if (error) {
        console.error("Error updating profile:", error.message);
      } else {
        // Fetch the updated user data
        const { data: userData, error: userError } =
          await supabase.auth.getUser();

        if (userError) {
          console.error("Error fetching user data:", userError.message);
        } else {
          setUser(userData.user);
          alert("Profile updated successfully!");
        }
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    }

    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error("Error logging out:", error.message);
      } else {
        setUser(null);
        navigate("/");
      }
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  if (!user) {
    return <div>Please log in to view your profile.</div>;
  }

  const themes = [
    "Light",
    "Dark",
    "Cupcake",
    "Bumblebee",
    "Emerald",
    "Corporate",
    "Synthwave",
    "Retro",
    "Cyberpunk",
    "Valentine",
    "Halloween",
    "Garden",
    "Forest",
    "Aqua",
    "Lo-Fi",
    "Pastel",
    "Fantasy",
    "Wireframe",
    "Black",
    "Luxury",
    "Dracula",
    "CMYK",
    "Autumn",
    "Business",
    "Acid",
    "Lemonade",
    "Night",
    "Coffee",
    "Winter",
    "Dim",
    "Nord",
    "Sunset",
  ];

  return (
    <Layout>
      <div className="mx-4 py-8">
        <div className="flex mt-8">
          <h1 className="text-3xl font-bold mb-8 grow">Profile</h1>
          <button
            className="btn btn-error"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
        <h2 className="font-bold text-lg mb-4">Info</h2>
        {isLoading && <center className="loading loading-spinner"></center>}
        {!isLoading && (
          <form
            onSubmit={handleUpdateProfile}
            className=""
          >
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">Username</span>
              </label>
              <div className="flex gap-4 flex-col sm:flex-row">
                <input
                  title="Username"
                  type="text"
                  className="input input-bordered grow"
                  value={profile.username ?? name}
                  onChange={(e) => setName(e.target.value)}
                />
                <button
                  type="submit"
                  className={`btn btn-primary ${loading ? "loading" : ""}`}
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Update Username"}
                </button>
              </div>
            </div>
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">Email</span>
              </label>
              <input
                title="Email"
                type="email"
                disabled
                className="input input-bordered"
                value={profile.email ?? email}
                readOnly
              />
            </div>
          </form>
        )}
        <div className="divider"></div>
        <h2 className="font-bold text-lg mb-4">Settings</h2>
        <div className="dropdown">
          <div
            tabIndex={0}
            role="button"
            className="btn"
          >
            Theme
            <svg
              width="12px"
              height="12px"
              className="h-2 w-2 fill-current opacity-60 inline-block"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 2048 2048"
            >
              <path d="M1799 349l242 241-1017 1017L7 590l242-241 775 775 775-775z"></path>
            </svg>
          </div>
          <ul
            tabIndex={0}
            className="dropdown-content z-[1] p-2 shadow-2xl bg-base-300 rounded-box w-52 max-h-96 overflow-y-auto"
          >
            {themes.map((theme) => (
              <ThemeOption
                key={theme}
                theme={theme}
              />
            ))}
          </ul>
        </div>
        <div className="divider"></div>

        <h2 className="font-bold text-lg mb-4">My Content</h2>
        <ul className="menu bg-base-200 rounded-box">
          <li>
            <a href="/snippet/me">My Snippets</a>
          </li>
          <li>
            <a href="/snippet/me/saves">My Saves</a>
          </li>
        </ul>
      </div>
    </Layout>
  );
};

export default UserEdit;
